// src/pages/ventes/EnvoyerEmailDevis.jsx
import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Alert, Spinner,
    Modal, InputGroup, Badge
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function EnvoyerEmailDevis() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [devis, setDevis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const [emailForm, setEmailForm] = useState({
        destinataire: '',
        cc: '',
        bcc: '',
        sujet: '',
        message: '',
        inclurePdf: true
    });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    useEffect(() => {
        fetchDevis();
    }, [id]);

    const fetchDevis = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/devis.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setDevis(result.data);
            if (result.data) {
                setEmailForm({
                    ...emailForm,
                    destinataire: result.data.client_email || '',
                    sujet: `Devis ${result.data.numero_proforma} - ${result.data.client_nom}`,
                    message: generateDefaultMessage(result.data)
                });
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const generateDefaultMessage = (devis) => {
        const validite = formatDate(devis.date_validite);
        return `Bonjour,

Nous avons le plaisir de vous adresser notre devis n° ${devis.numero_proforma} pour les prestations demandées.

Vous trouverez en pièce jointe le détail de notre proposition.

Ce devis est valable jusqu'au ${validite}. Restant à votre disposition pour tout renseignement complémentaire, nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations distinguées.

Cordialement,
L'équipe GESTION STOCK`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!emailForm.destinataire) {
            showToast('error', 'Champ requis', 'Veuillez saisir une adresse email');
            return;
        }

        setSending(true);
        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;

            const response = await fetch(`${API_BASE_URL}/send-email.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    to: emailForm.destinataire,
                    cc: emailForm.cc,
                    bcc: emailForm.bcc,
                    subject: emailForm.sujet,
                    message: emailForm.message,
                    devis_id: id,
                    include_pdf: emailForm.inclurePdf,
                    id_utilisateur: user?.id || user?.id_utilisateur
                })
            });

            const result = await response.json();

            if (result.success) {
                showToast('success', 'Email envoyé', 'Le devis a été envoyé avec succès');
                setTimeout(() => navigate(`/ventes/DetailDevis/${id}`), 2000);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de l'envoi de l'email");
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur lors de l'envoi de l'email");
        } finally {
            setSending(false);
        }
    };

    const getStatutConfig = (statut) => {
        const configs = {
            brouillon:        { gradient: 'linear-gradient(135deg, #64748b, #475569)', icon: 'ri-draft-line',        label: 'Brouillon' },
            envoye:           { gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', icon: 'ri-send-plane-line',   label: 'Envoyé' },
            accepte:          { gradient: 'linear-gradient(135deg, #10b981, #059669)', icon: 'ri-check-double-line', label: 'Accepté' },
            refuse:           { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', icon: 'ri-close-circle-line', label: 'Refusé' },
            converti_facture: { gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)', icon: 'ri-file-transfer-line', label: 'Converti' }
        };
        return configs[statut] || configs.brouillon;
    };

    const getStatutBadge = (statut) => {
        const config = getStatutConfig(statut);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    /* ============================================================
       LOADING / ERROR
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-mail-send-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement du devis
                        </h5>
                        <p className="text-muted small">Récupération des informations en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!devis) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Devis non trouvé</h6>
                                <p className="mb-0 small">Le devis que vous recherchez n'existe pas</p>
                            </div>
                            <Button onClick={() => navigate('/ventes/devisListe')} className="btn-modern-outline">
                                <i className="ri-arrow-left-line me-1"></i>Retour
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-mail-send-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Envoyer le devis par email</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-file-copy-line"></i>
                                        {devis.numero_proforma}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-user-line"></i>
                                        {devis.client_nom}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate(`/ventes/DetailDevis/${id}`)}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            <Button className="btn-modern-secondary" onClick={() => setShowPreviewModal(true)}>
                                <i className="ri-eye-line"></i> Aperçu
                            </Button>
                        </div>
                    </div>

                    <Row className="g-4">
                        {/* ===== FORMULAIRE D'ENVOI ===== */}
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-mail-send-line me-2"></i>
                                        Composition de l'email
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-3">
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Destinataire <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            value={emailForm.destinataire}
                                                            onChange={(e) => setEmailForm({...emailForm, destinataire: e.target.value})}
                                                            placeholder="client@email.com"
                                                            required
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        <i className="ri-information-line me-1"></i>
                                                        Email du client
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">CC</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-file-copy-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            value={emailForm.cc}
                                                            onChange={(e) => setEmailForm({...emailForm, cc: e.target.value})}
                                                            placeholder="cc@email.com"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">BCC</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-eye-off-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            value={emailForm.bcc}
                                                            onChange={(e) => setEmailForm({...emailForm, bcc: e.target.value})}
                                                            placeholder="bcc@email.com"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Sujet</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-quill-pen-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            value={emailForm.sujet}
                                                            onChange={(e) => setEmailForm({...emailForm, sujet: e.target.value})}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Message</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={10}
                                                        value={emailForm.message}
                                                        onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                                                        className="textarea-modern"
                                                        required
                                                    />
                                                    <Form.Text className="text-muted small">
                                                        <i className="ri-information-line me-1"></i>
                                                        Personnalisez le message selon vos besoins
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <div className="checkbox-modern-card">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="include-pdf"
                                                        checked={emailForm.inclurePdf}
                                                        onChange={(e) => setEmailForm({...emailForm, inclurePdf: e.target.checked})}
                                                        label={
                                                            <div className="d-flex align-items-center gap-2">
                                                                <i className="ri-attachment-2"></i>
                                                                <span className="fw-semibold">Joindre le devis au format PDF</span>
                                                            </div>
                                                        }
                                                    />
                                                </div>
                                            </Col>

                                            <Col md={12}>
                                                <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                                                <div className="d-flex justify-content-end gap-3 flex-wrap">
                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={() => navigate(`/ventes/DetailDevis/${id}`)}
                                                        className="btn-modern-outline"
                                                    >
                                                        Annuler
                                                    </Button>
                                                    <Button
                                                        type="submit"
                                                        disabled={sending}
                                                        className="btn-modern-warning"
                                                    >
                                                        {sending ? (
                                                            <><Spinner size="sm" className="me-2" />Envoi en cours...</>
                                                        ) : (
                                                            <><i className="ri-send-plane-line me-2"></i>Envoyer</>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* ===== RÉCAPITULATIF ===== */}
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm rounded-3 mb-4 sticky-top" style={{ top: '20px' }}>
                                <Card.Header className="card-header-gradient">
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-information-line me-2"></i>
                                        Récapitulatif
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <div className="recap-block-charte">
                                        <small className="recap-label-charte">Devis</small>
                                        <h5 className="recap-value-charte" style={{ color: COLORS.primary }}>
                                            {devis.numero_proforma}
                                        </h5>
                                    </div>

                                    <div className="recap-block-charte">
                                        <small className="recap-label-charte">Client</small>
                                        <h6 className="mb-1 fw-semibold">{devis.client_nom}</h6>
                                        <p className="text-muted small mb-0">
                                            <i className="ri-barcode-line me-1"></i>{devis.code_client}
                                        </p>
                                    </div>

                                    <div className="recap-block-charte">
                                        <small className="recap-label-charte">Montant total</small>
                                        <h4 className="recap-value-charte" style={{ color: COLORS.primary }}>
                                            {formatCurrency(devis.net_a_payer)}
                                        </h4>
                                    </div>

                                    <div className="recap-block-charte">
                                        <small className="recap-label-charte">Validité</small>
                                        <p className="mb-0 fw-semibold">
                                            <i className="ri-calendar-line me-1" style={{ color: COLORS.secondary }}></i>
                                            {formatDate(devis.date_validite)}
                                        </p>
                                    </div>

                                    <hr style={{ borderColor: '#e2e8f0' }} />

                                    <div className="recap-block-charte">
                                        <small className="recap-label-charte">Statut</small>
                                        <div className="mt-2">{getStatutBadge(devis.statut)}</div>
                                    </div>

                                    {devis.notes && (
                                        <>
                                            <hr style={{ borderColor: '#e2e8f0' }} />
                                            <div>
                                                <small className="recap-label-charte">Notes</small>
                                                <p className="small mt-2 mb-0 text-muted">{devis.notes}</p>
                                            </div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-3 info-card-charte">
                                <Card.Body className="p-4">
                                    <div className="section-title-charte">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                            <i className="ri-information-line"></i>
                                        </span>
                                        <h6>Informations</h6>
                                    </div>
                                    <ul className="info-list-charte">
                                        <li>
                                            <i className="ri-mail-line"></i>
                                            <span>L'email sera envoyé avec le devis en pièce jointe</span>
                                        </li>
                                        <li>
                                            <i className="ri-user-line"></i>
                                            <span>Le client recevra une copie du devis</span>
                                        </li>
                                        <li>
                                            <i className="ri-refresh-line"></i>
                                            <span>Le statut du devis sera mis à jour en « Envoyé »</span>
                                        </li>
                                    </ul>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL APERÇU EMAIL ===== */}
            <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-mail-send-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Aperçu de l'email</Modal.Title>
                            <p className="text-white-50 mb-0 small">Vérifiez le contenu avant envoi</p>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="p-4">
                    <div className="email-preview-container-charte">
                        {/* En-tête de l'email */}
                        <div className="email-header-card-charte mb-4">
                            <div className="email-header-title-charte">
                                <i className="ri-mail-line me-2"></i>
                                Informations d'envoi
                            </div>
                            <div className="email-header-content-charte">
                                <div className="email-info-row-charte">
                                    <div className="email-info-label-charte">
                                        <i className="ri-send-plane-line"></i>
                                        De :
                                    </div>
                                    <div className="email-info-value-charte">
                                        <span className="fw-semibold">GESTION STOCK</span>
                                        <span className="text-muted mx-1">&lt;contact@gestionstock.ga&gt;</span>
                                    </div>
                                </div>

                                <div className="email-info-row-charte">
                                    <div className="email-info-label-charte">
                                        <i className="ri-user-line"></i>
                                        À :
                                    </div>
                                    <div className="email-info-value-charte">
                                        <span className="fw-semibold" style={{ color: COLORS.primary }}>
                                            {emailForm.destinataire || 'Non spécifié'}
                                        </span>
                                    </div>
                                </div>

                                {emailForm.cc && (
                                    <div className="email-info-row-charte">
                                        <div className="email-info-label-charte">
                                            <i className="ri-file-copy-line"></i>
                                            CC :
                                        </div>
                                        <div className="email-info-value-charte">
                                            <span className="text-muted">{emailForm.cc}</span>
                                        </div>
                                    </div>
                                )}

                                {emailForm.bcc && (
                                    <div className="email-info-row-charte">
                                        <div className="email-info-label-charte">
                                            <i className="ri-eye-off-line"></i>
                                            BCC :
                                        </div>
                                        <div className="email-info-value-charte">
                                            <span className="text-muted">{emailForm.bcc}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="email-info-row-charte">
                                    <div className="email-info-label-charte">
                                        <i className="ri-quill-pen-line"></i>
                                        Sujet :
                                    </div>
                                    <div className="email-info-value-charte">
                                        <span className="fw-bold">{emailForm.sujet}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Corps du message */}
                        <div className="email-body-card-charte mb-4">
                            <div className="email-body-title-charte">
                                <i className="ri-chat-3-line me-2"></i>
                                Message
                            </div>
                            <div className="email-body-content-charte">
                                {emailForm.message.split('\n').map((paragraph, idx) => (
                                    <p key={idx} className="mb-2">{paragraph || <br />}</p>
                                ))}
                            </div>
                        </div>

                        {/* Pièce jointe */}
                        {emailForm.inclurePdf && (
                            <div className="email-attachment-card-charte">
                                <div className="attachment-icon-charte">
                                    <i className="ri-file-pdf-line"></i>
                                </div>
                                <div className="attachment-info-charte">
                                    <div className="attachment-name-charte">
                                        Devis_{devis?.numero_proforma}.pdf
                                    </div>
                                    <div className="attachment-size-charte">
                                        Document PDF • ~150 KB
                                    </div>
                                </div>
                                <div className="attachment-badge-charte">
                                    <span className="badge-attached-charte">
                                        <i className="ri-attachment-line"></i>
                                        Joint
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Note de confirmation */}
                        <div className="email-footer-note-charte mt-3">
                            <i className="ri-information-line me-1"></i>
                            Vérifiez les informations avant d'envoyer l'email
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowPreviewModal(false)} className="btn-modern-outline">
                        <i className="ri-close-line me-2"></i>Fermer
                    </Button>
                    <Button
                        onClick={() => {
                            setShowPreviewModal(false);
                            handleSubmit(new Event('submit'));
                        }}
                        disabled={sending}
                        className="btn-modern-primary"
                    >
                        {sending ? (
                            <><Spinner size="sm" className="me-2" />Envoi...</>
                        ) : (
                            <><i className="ri-mail-send-line me-2"></i>Envoyer l'email</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.35rem 0.9rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .textarea-modern {
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 1rem;
                    font-size: 0.9rem;
                    font-family: 'Inter', -apple-system, sans-serif;
                    line-height: 1.6;
                    transition: all 0.2s ease;
                    resize: vertical;
                }
                .textarea-modern:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .checkbox-modern-card {
                    background: #f8fafc;
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.85rem 1.1rem;
                    transition: all 0.2s ease;
                }
                .checkbox-modern-card:hover {
                    border-color: var(--secondary);
                    background: #f0fbff;
                }
                .checkbox-modern-card .form-check-input:checked {
                    background-color: var(--secondary);
                    border-color: var(--secondary);
                }
                .checkbox-modern-card .form-check-input {
                    width: 20px; height: 20px;
                    margin-top: 0.15rem;
                    cursor: pointer;
                }
                .checkbox-modern-card .form-check-label {
                    cursor: pointer;
                    color: #334155;
                }

                /* ===== RÉCAP ===== */
                .recap-block-charte {
                    margin-bottom: 1.1rem;
                }
                .recap-label-charte {
                    display: block;
                    color: #94a3b8;
                    font-size: 0.7rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 0.4rem;
                }
                .recap-value-charte {
                    font-weight: 700;
                    margin-bottom: 0;
                }

                /* ===== INFO CARD ===== */
                .info-card-charte {
                    background: linear-gradient(135deg, #ffffff, #f8fafc);
                }
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }
                .info-list-charte {
                    list-style: none; padding: 0; margin: 0;
                }
                .info-list-charte li {
                    display: flex; align-items: flex-start; gap: 0.75rem;
                    padding: 0.6rem 0;
                    font-size: 0.85rem;
                    color: #475569;
                    border-bottom: 1px dashed #f1f5f9;
                }
                .info-list-charte li:last-child { border-bottom: none; }
                .info-list-charte li i {
                    color: var(--secondary);
                    font-size: 1rem;
                    margin-top: 0.15rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary, .btn-modern-secondary,
                .btn-modern-warning {
                    border: none; color: white; padding: 0.55rem 1.8rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-secondary {
                    background: linear-gradient(135deg, var(--secondary), #0d8ba6);
                    box-shadow: 0 4px 12px rgba(33,172,205,0.3);
                }
                .btn-modern-secondary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(33,172,205,0.4); color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4); color: white;
                }
                .btn-modern-primary:disabled,
                .btn-modern-warning:disabled { opacity: 0.6; transform: none; }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== EMAIL PREVIEW ===== */
                .email-preview-container-charte {
                    max-height: 60vh;
                    overflow-y: auto;
                    padding-right: 8px;
                }
                .email-preview-container-charte::-webkit-scrollbar { width: 6px; }
                .email-preview-container-charte::-webkit-scrollbar-track {
                    background: #f1f5f9; border-radius: 10px;
                }
                .email-preview-container-charte::-webkit-scrollbar-thumb {
                    background: #cbd5e1; border-radius: 10px;
                }
                .email-preview-container-charte::-webkit-scrollbar-thumb:hover {
                    background: var(--secondary);
                }

                .email-header-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    border-radius: 14px;
                    border: 1px solid #e2e8f0;
                    overflow: hidden;
                }
                .email-header-title-charte {
                    background: linear-gradient(135deg, ${COLORS.primary}10, ${COLORS.secondary}10);
                    padding: 0.85rem 1.25rem;
                    font-weight: 600;
                    color: var(--primary);
                    border-bottom: 1px solid #e2e8f0;
                    font-size: 0.9rem;
                }
                .email-header-content-charte { padding: 1.25rem; }
                .email-info-row-charte {
                    display: flex; margin-bottom: 0.85rem; flex-wrap: wrap;
                }
                .email-info-row-charte:last-child { margin-bottom: 0; }
                .email-info-label-charte {
                    width: 80px; color: #64748b;
                    font-size: 0.85rem;
                    display: flex; align-items: center; gap: 6px;
                }
                .email-info-label-charte i {
                    font-size: 1rem; color: var(--secondary);
                }
                .email-info-value-charte {
                    flex: 1; font-size: 0.9rem; color: #1e293b;
                    word-break: break-word;
                }

                .email-body-card-charte {
                    background: white;
                    border-radius: 14px;
                    border: 1px solid #e2e8f0;
                    overflow: hidden;
                }
                .email-body-title-charte {
                    background: linear-gradient(135deg, ${COLORS.primary}10, ${COLORS.secondary}10);
                    padding: 0.85rem 1.25rem;
                    font-weight: 600;
                    color: var(--primary);
                    border-bottom: 1px solid #e2e8f0;
                    font-size: 0.9rem;
                }
                .email-body-content-charte {
                    padding: 1.25rem;
                    background: #fafcff;
                    font-size: 0.9rem;
                    line-height: 1.7;
                    color: #334155;
                    max-height: 280px;
                    overflow-y: auto;
                }

                .email-attachment-card-charte {
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    padding: 1rem 1.25rem;
                    display: flex; align-items: center; gap: 1rem;
                    transition: all 0.2s ease;
                }
                .email-attachment-card-charte:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.05);
                }
                .attachment-icon-charte {
                    width: 44px; height: 44px;
                    background: linear-gradient(135deg, #fee2e2, #fecaca);
                    border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .attachment-icon-charte i {
                    font-size: 1.5rem; color: #dc2626;
                }
                .attachment-info-charte { flex: 1; min-width: 0; }
                .attachment-name-charte {
                    font-weight: 600; color: #1e293b;
                    font-size: 0.9rem;
                }
                .attachment-size-charte {
                    font-size: 0.7rem; color: #94a3b8;
                }
                .badge-attached-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: linear-gradient(135deg, ${COLORS.success}, #059669);
                    color: white;
                    padding: 0.35rem 0.85rem;
                    border-radius: 50px;
                    font-size: 0.7rem; font-weight: 600;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                }

                .email-footer-note-charte {
                    padding: 0.85rem;
                    background: linear-gradient(135deg, ${COLORS.info}08, ${COLORS.info}15);
                    border-radius: 12px;
                    text-align: center;
                    font-size: 0.8rem;
                    color: var(--info);
                    border: 1px solid ${COLORS.info}20;
                }

                /* ===== ALERTE ERREUR ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .email-info-row-charte { flex-direction: column; gap: 4px; }
                    .email-info-label-charte { width: 100%; }
                    .email-attachment-card-charte { flex-direction: column; text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}